import { useState } from "react";
import { usePromptStore } from "../../../hooks/usePromptStore";
import { classScenarios } from "../../../mocks/classContent";

/* ─────────────────────────────────────────────
   Types
───────────────────────────────────────────── */
interface BlockOption {
  label: string;
  icon: string;
  color: string;
  bg: string;
  options: string[];
}
type BlockSet = Record<string, BlockOption>;

/* ─────────────────────────────────────────────
   Block sets per category
───────────────────────────────────────────── */
const IMAGE_BLOCKS: BlockSet = {
  subject: { label: "주제", icon: "ri-focus-3-line", color: "#FF8C00", bg: "#FFF3E0",
    options: ["귀여운 캐릭터", "풍경 / 자연", "제품 목업", "추상적 패턴", "음식 & 음료", "건축물"] },
  style: { label: "스타일", icon: "ri-magic-line", color: "#7C3AED", bg: "#EDE7F6",
    options: ["flat illustration", "3D render", "watercolor", "photorealistic", "pixel art", "minimalist"] },
  mood: { label: "분위기", icon: "ri-emotion-happy-line", color: "#E91E63", bg: "#FCE4EC",
    options: ["cute, kawaii", "dark & mysterious", "warm & cozy", "futuristic, neon", "soft pastel", "bold & vibrant"] },
  bg: { label: "배경", icon: "ri-image-2-line", color: "#00D873", bg: "#E8F5E9",
    options: ["white background", "gradient background", "transparent background", "bokeh background", "studio lighting", "outdoor natural light"] },
  param: { label: "파라미터", icon: "ri-settings-3-line", color: "#1565C0", bg: "#E3F2FD",
    options: ["--v 6", "--ar 1:1", "--ar 16:9", "--ar 3:4", "--style raw", "--q 2"] },
};

const WRITING_BLOCKS: BlockSet = {
  role: { label: "역할 설정", icon: "ri-user-star-line", color: "#9C27B0", bg: "#F3E5F5",
    options: ["전문 카피라이터", "유튜브 스크립트 작가", "SNS 마케터", "콘텐츠 기획자", "브랜드 에디터", "블로그 작가"] },
  target: { label: "타겟 독자", icon: "ri-group-2-line", color: "#E91E63", bg: "#FCE4EC",
    options: ["20대 여성", "Gen-Z 한국인", "직장인 30대", "뷰티 관심층", "자기계발 독자", "스타트업 창업자"] },
  format: { label: "출력 형식", icon: "ri-layout-line", color: "#FF8C00", bg: "#FFF3E0",
    options: ["인스타그램 캡션", "유튜브 스크립트", "이메일 뉴스레터", "블로그 포스팅", "광고 카피", "제품 설명문"] },
  tone: { label: "말투 & 톤", icon: "ri-volume-up-line", color: "#00D873", bg: "#E8F5E9",
    options: ["캐주얼 & 친근", "전문적 & 신뢰감", "유머러스 & 위트", "감성적 & 공감", "강렬 & 직설적", "따뜻 & 공감형"] },
  length: { label: "글 길이", icon: "ri-text-wrap", color: "#1565C0", bg: "#E3F2FD",
    options: ["50단어 이내", "150단어 내외", "300단어 내외", "500단어 이상", "3문단", "5문단 이상"] },
};

const BUSINESS_BLOCKS: BlockSet = {
  role: { label: "역할 지정", icon: "ri-briefcase-4-line", color: "#00D873", bg: "#E8F5E9",
    options: ["전문 보고서 작성자", "데이터 분석가", "프로젝트 매니저", "마케팅 전략가", "콘텐츠 편집자", "이메일 전문가"] },
  task: { label: "작업 내용", icon: "ri-task-line", color: "#FF8C00", bg: "#FFF3E0",
    options: ["회의록 요약", "주간 보고서 작성", "이메일 초안 작성", "데이터 표 정리", "아이디어 브레인스토밍", "업무 체크리스트 생성"] },
  outputFormat: { label: "출력 형식", icon: "ri-file-list-3-line", color: "#7C3AED", bg: "#EDE7F6",
    options: ["표(Table)", "불릿 리스트", "JSON 형식", "마크다운", "단락 요약", "번호 목록"] },
  conditions: { label: "조건 & 제약", icon: "ri-shield-check-line", color: "#E91E63", bg: "#FCE4EC",
    options: ["200단어 이내로", "한국어로 응답", "핵심만 3가지", "실행 가능한 내용만", "전문 용어 사용 금지", "데이터 근거 포함"] },
  context: { label: "배경 상황", icon: "ri-building-2-line", color: "#1565C0", bg: "#E3F2FD",
    options: ["스타트업 환경", "대기업 팀 보고", "1인 사업자", "마케팅 팀", "개발팀 협업", "임원 보고용"] },
};

const VIDEO_BLOCKS: BlockSet = {
  camera: { label: "카메라 무브먼트", icon: "ri-camera-2-line", color: "#F9A825", bg: "#FFF9C4",
    options: ["slow push-in", "dolly zoom", "aerial drone", "handheld shaky", "static shot", "360 orbit"] },
  subject: { label: "주요 피사체", icon: "ri-focus-line", color: "#E91E63", bg: "#FCE4EC",
    options: ["도시 야경", "자연 풍경", "인물 클로즈업", "음식 & 음료", "제품 클로즈업", "추상적 패턴"] },
  motion: { label: "움직임 효과", icon: "ri-sparkling-line", color: "#7C3AED", bg: "#EDE7F6",
    options: ["부드러운 카메라 이동", "파티클 효과", "빛 번짐 보케", "슬로우 모션", "타임랩스", "불꽃 파티클"] },
  mood: { label: "분위기", icon: "ri-emotion-line", color: "#00D873", bg: "#E8F5E9",
    options: ["cinematic, dramatic", "dreamy, soft", "energetic, vibrant", "dark, moody", "warm golden hour", "cool neon night"] },
  duration: { label: "영상 길이", icon: "ri-time-line", color: "#FF8C00", bg: "#FFF3E0",
    options: ["4초", "8초", "15초", "30초", "1분", "3분"] },
};

const DEV_BLOCKS: BlockSet = {
  role: { label: "AI 역할 설정", icon: "ri-robot-2-line", color: "#1565C0", bg: "#E3F2FD",
    options: ["친절한 고객 서비스", "전문 기술 지원", "언어 학습 튜터", "요리 레시피 도우미", "여행 가이드", "HR 인터뷰어"] },
  task: { label: "핵심 기능", icon: "ri-function-line", color: "#00D873", bg: "#E8F5E9",
    options: ["질문 답변", "메뉴 주문 처리", "예약 관리", "FAQ 자동 응답", "코드 리뷰", "번역 & 요약"] },
  outputFormat: { label: "응답 형식", icon: "ri-message-3-line", color: "#9C27B0", bg: "#F3E5F5",
    options: ["친근한 말투", "공식적 존댓말", "간결한 한 줄", "단계별 안내", "이모지 포함", "리스트 형태"] },
  constraints: { label: "제약 조건", icon: "ri-lock-line", color: "#E91E63", bg: "#FCE4EC",
    options: ["메뉴 외 질문 거절", "한국어만 응답", "200자 이내로", "개인정보 수집 금지", "경쟁사 언급 금지", "항상 CTA 포함"] },
  temperature: { label: "창의성 수준", icon: "ri-equalizer-line", color: "#FF8C00", bg: "#FFF3E0",
    options: ["Temperature 0.2 (일관성)", "Temperature 0.5 (균형)", "Temperature 0.7 (창의적)", "Temperature 0.9 (자유로운)"] },
};

const CATEGORY_CONFIG: Record<string, { blocks: BlockSet; title: string; desc: string; promptHint: string }> = {
  image:    { blocks: IMAGE_BLOCKS,    title: "이미지 프롬프트 빌더",        desc: "블록을 조합해서 AI 이미지 프롬프트를 완성해보세요.",                       promptHint: "이미지 생성 프롬프트" },
  writing:  { blocks: WRITING_BLOCKS,  title: "글쓰기 프롬프트 빌더",        desc: "역할, 타겟, 형식, 톤을 설정해서 최적의 글쓰기 프롬프트를 만들어보세요.",     promptHint: "글쓰기 프롬프트" },
  business: { blocks: BUSINESS_BLOCKS, title: "업무 자동화 프롬프트 빌더",    desc: "역할과 작업, 출력 형식을 지정해서 업무 자동화 프롬프트를 완성해보세요.",     promptHint: "업무 자동화 프롬프트" },
  video:    { blocks: VIDEO_BLOCKS,    title: "영상 프롬프트 빌더",          desc: "카메라 무브먼트, 피사체, 분위기를 설정해서 영상 프롬프트를 완성해보세요.",    promptHint: "영상 생성 프롬프트" },
  dev:      { blocks: DEV_BLOCKS,      title: "챗봇 System Prompt 빌더",    desc: "역할, 기능, 제약 조건을 설정해서 나만의 챗봇 프롬프트를 만들어보세요.",       promptHint: "System Prompt" },
};

/* ─────────────────────────────────────────────
   Prompt builder logic
───────────────────────────────────────────── */
function buildPromptByCategory(category: string, selected: Record<string, string[]>, customText: string): string {
  const parts: string[] = [];
  if (category === "image") {
    if (selected.subject?.length) parts.push(selected.subject.join(", "));
    if (customText.trim()) parts.push(customText.trim());
    if (selected.style?.length)   parts.push(selected.style.join(", "));
    if (selected.mood?.length)    parts.push(selected.mood.join(", "));
    if (selected.bg?.length)      parts.push(selected.bg.join(", "));
    if (selected.param?.length)   parts.push(selected.param.join(" "));
  } else if (category === "writing") {
    if (selected.role?.length)   parts.push(`Act as a ${selected.role.join(" & ")}.`);
    if (selected.format?.length) parts.push(`Write ${selected.format.join(" and ")}`);
    if (selected.target?.length) parts.push(`for ${selected.target.join(", ")}`);
    if (selected.tone?.length)   parts.push(`Tone: ${selected.tone.join(", ")}.`);
    if (selected.length?.length) parts.push(`Length: ${selected.length.join(", ")}.`);
    if (customText.trim())       parts.push(customText.trim());
  } else if (category === "business") {
    if (selected.role?.length)         parts.push(`Act as a ${selected.role.join(" & ")}.`);
    if (selected.task?.length)         parts.push(`Task: ${selected.task.join(", ")}.`);
    if (selected.outputFormat?.length) parts.push(`Output format: ${selected.outputFormat.join(", ")}.`);
    if (selected.conditions?.length)   parts.push(`Constraints: ${selected.conditions.join("; ")}.`);
    if (selected.context?.length)      parts.push(`Context: ${selected.context.join(", ")}.`);
    if (customText.trim())             parts.push(customText.trim());
  } else if (category === "video") {
    if (selected.camera?.length)   parts.push(`Camera: ${selected.camera.join(", ")}`);
    if (selected.subject?.length)  parts.push(`Subject: ${selected.subject.join(", ")}`);
    if (selected.motion?.length)   parts.push(`Motion: ${selected.motion.join(", ")}`);
    if (selected.mood?.length)     parts.push(`Mood: ${selected.mood.join(", ")}`);
    if (selected.duration?.length) parts.push(`Duration: ${selected.duration.join(", ")}`);
    if (customText.trim())         parts.push(customText.trim());
  } else if (category === "dev") {
    if (selected.role?.length)         parts.push(`You are a ${selected.role.join(" & ")}.`);
    if (selected.task?.length)         parts.push(`Primary task: ${selected.task.join(", ")}.`);
    if (selected.outputFormat?.length) parts.push(`Response style: ${selected.outputFormat.join(", ")}.`);
    if (selected.constraints?.length)  parts.push(`Rules: ${selected.constraints.join("; ")}.`);
    if (selected.temperature?.length)  parts.push(selected.temperature[0]);
    if (customText.trim())             parts.push(customText.trim());
  }
  return parts.join(" ");
}

/* ─────────────────────────────────────────────
   AI-PBL Scenario Card
───────────────────────────────────────────── */
function ScenarioCard({ classId }: { classId: number }) {
  const [collapsed, setCollapsed] = useState(false);
  const scenario = classScenarios[classId];
  if (!scenario) return null;

  return (
    <div className="mb-7 rounded-2xl overflow-hidden border-2 border-[#00D873]/30">
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-[#E8F5E9] to-[#F0FDF7] cursor-pointer hover:opacity-90 transition-opacity"
      >
        <div className="w-7 h-7 rounded-xl bg-[#00D873] flex items-center justify-center flex-shrink-0">
          <i className="ri-map-pin-user-line text-white text-sm" />
        </div>
        <div className="flex-1 text-left">
          <span className="text-[10px] font-bold text-[#00D873] tracking-widest uppercase mr-2">AI-PBL 시나리오</span>
          <span className="text-xs font-bold bg-[#00D873]/15 text-[#1A7A45] px-2 py-0.5 rounded-full">{scenario.tag}</span>
        </div>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-[#00D873] text-lg transition-transform duration-200 ${collapsed ? "" : "rotate-180"}`} />
        </div>
      </button>

      {!collapsed && (
        <div className="bg-white px-5 py-5 space-y-4">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-[#191F28] flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-user-line text-white text-xs" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-[#8B95A1] uppercase tracking-widest mb-0.5">나의 역할</p>
              <p className="text-sm font-bold text-[#191F28] leading-snug">{scenario.persona}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-[#FFF3E0] flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-alarm-warning-line text-[#FF8C00] text-xs" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-[#8B95A1] uppercase tracking-widest mb-0.5">현재 상황</p>
              <p className="text-sm text-[#3D4756] leading-[1.75]">{scenario.situation}</p>
            </div>
          </div>
          <div className="bg-[#191F28] rounded-xl px-4 py-3 flex gap-3">
            <i className="ri-focus-3-line text-[#00D873] text-base flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-bold text-[#00D873] uppercase tracking-widest mb-0.5">이번 챌린지 목표</p>
              <p className="text-sm font-semibold text-white leading-snug">{scenario.goal}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Save Toast
───────────────────────────────────────────── */
function SaveToast({ show }: { show: boolean }) {
  return (
    <div
      className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-[#191F28] text-white px-5 py-3 rounded-2xl text-sm font-bold flex items-center gap-2 transition-all duration-300 ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <i className="ri-book-mark-line text-[#00D873]" />
      노트북에 저장됐어요!
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
interface Props {
  onComplete: (prompt: string) => void;
  classTitle: string;
  classId?: number;
  category?: string;
  fromAnalyze?: boolean;
}

export default function PracticeStep({ onComplete, classTitle, classId = 0, category = "image", fromAnalyze = false }: Props) {
  const config = CATEGORY_CONFIG[category] ?? CATEGORY_CONFIG.image;
  const { blocks, title, desc, promptHint } = config;
  const blockKeys = Object.keys(blocks);

  const [selected, setSelected] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(blockKeys.map((k) => [k, []]))
  );
  const [customText, setCustomText] = useState("");
  const [showSaveToast, setShowSaveToast] = useState(false);
  const { savePrompt, isPromptSaved } = usePromptStore();

  const toggle = (blockKey: string, option: string) => {
    setSelected((prev) => {
      const current = prev[blockKey] ?? [];
      if (current.includes(option)) return { ...prev, [blockKey]: current.filter((o) => o !== option) };
      const limit = blockKey === "param" || blockKey === "temperature" ? 1 : 2;
      if (current.length >= limit) return { ...prev, [blockKey]: [...current.slice(1), option] };
      return { ...prev, [blockKey]: [...current, option] };
    });
  };

  const promptPreview = buildPromptByCategory(category, selected, customText);
  const hasContent = promptPreview.trim().length > 0;
  const alreadySaved = hasContent && isPromptSaved(promptPreview);

  const handleSave = () => {
    if (!hasContent || alreadySaved) return;
    savePrompt(promptPreview, classTitle, classId);
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 2500);
  };

  return (
    <>
      <SaveToast show={showSaveToast} />
      <div className="bg-white rounded-2xl p-8 mb-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-tools-line text-[#00D873] text-lg" />
          </div>
          <span className="text-xs font-bold text-[#00D873] tracking-widest uppercase">프롬프트 빌더</span>
        </div>
        <h2 className="text-2xl font-extrabold text-[#191F28] mb-1">{title}</h2>
        <p className="text-[#8B95A1] text-sm mb-6">{desc}</p>

        {/* AI-PBL Scenario Card */}
        <ScenarioCard classId={classId} />

        {/* Analyze → Create bridge banner */}
        {fromAnalyze && (
          <div className="mb-6 rounded-2xl overflow-hidden border border-orange-200">
            <div className="bg-gradient-to-r from-orange-50 to-[#FFF9F0] px-5 py-4 flex items-center gap-3">
              <div className="flex-shrink-0 flex items-center gap-1">
                <span className="w-7 h-7 rounded-full bg-[#FF8C00] flex items-center justify-center text-white text-xs font-extrabold">A</span>
                <i className="ri-arrow-right-line text-[#8B95A1] text-sm" />
                <span className="w-7 h-7 rounded-full bg-[#00D873] flex items-center justify-center text-white text-xs font-extrabold">C</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-extrabold text-[#191F28]">분석 완료! ACE의 C 단계로 넘어가요 ⚡</p>
                <p className="text-xs text-[#8B95A1] mt-0.5">방금 AI 오류를 찾아냈어요. 이제 직접 프롬프트를 만들어보세요!</p>
              </div>
              <span className="flex-shrink-0 text-[10px] font-bold px-2 py-1 bg-[#00D873] text-white rounded-full whitespace-nowrap">
                ACE 콤보 🔥
              </span>
            </div>
          </div>
        )}

        {/* Block Sections */}
        <div className="space-y-6">
          {blockKeys.map((key) => {
            const block = blocks[key];
            const sel = selected[key] ?? [];
            return (
              <div key={key}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: block.bg }}>
                    <i className={`${block.icon} text-sm`} style={{ color: block.color }} />
                  </div>
                  <span className="text-sm font-bold text-[#191F28]">{block.label}</span>
                  <span className="text-xs text-[#8B95A1]">
                    {sel.length > 0 ? `${sel.length}개 선택됨` : "클릭해서 선택"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {block.options.map((opt) => {
                    const isSelected = sel.includes(opt);
                    return (
                      <button
                        key={opt}
                        onClick={() => toggle(key, opt)}
                        className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          isSelected ? "text-white" : "bg-[#F2F4F6] text-[#3D4756] hover:bg-[#E8E9EB]"
                        }`}
                        style={isSelected ? { backgroundColor: block.color } : {}}
                      >
                        {isSelected && <span className="mr-1">✓</span>}
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Custom input */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-[#F2F4F6]">
                <i className="ri-edit-line text-sm text-[#8B95A1]" />
              </div>
              <span className="text-sm font-bold text-[#191F28]">직접 입력</span>
            </div>
            <input
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="어떤 업무를 AI에게 맡기고 싶으신가요? (예: 인스타 릴스 대본 짜줘)"
              className="w-full bg-[#F2F4F6] rounded-xl px-4 py-3 text-sm text-[#191F28] placeholder-[#8B95A1] outline-none focus:ring-2 focus:ring-[#00D873]/30 transition-all"
            />
          </div>
        </div>

        {/* Prompt Preview */}
        <div className="mt-8 rounded-2xl overflow-hidden border-2 border-dashed border-[#00D873]/30">
          <div className="bg-[#191F28] px-5 py-3 flex items-center justify-between gap-3">
            <span className="text-xs font-bold text-[#8B95A1] tracking-widest uppercase">{promptHint}</span>
            {hasContent && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigator.clipboard?.writeText(promptPreview).catch(() => {})}
                  className="text-xs text-[#8B95A1] hover:text-white font-bold cursor-pointer whitespace-nowrap flex items-center gap-1 transition-colors"
                >
                  <i className="ri-file-copy-line" />복사
                </button>
                <button
                  onClick={handleSave}
                  disabled={alreadySaved}
                  className={`text-xs font-bold cursor-pointer whitespace-nowrap flex items-center gap-1 transition-colors ${
                    alreadySaved ? "text-[#00D873]/50" : "text-[#00D873] hover:text-[#00D873]/80"
                  }`}
                >
                  <i className={alreadySaved ? "ri-book-mark-fill" : "ri-book-mark-line"} />
                  {alreadySaved ? "저장됨" : "노트북 저장"}
                </button>
              </div>
            )}
          </div>
          <div className="bg-[#0D1117] px-5 py-4 min-h-[80px] flex items-start">
            {hasContent
              ? <code className="text-[#00D873] text-sm font-mono leading-relaxed break-words w-full">{promptPreview}</code>
              : <span className="text-[#3D4756] text-sm font-mono">블록을 선택하면 여기에 프롬프트가 완성돼요...</span>
            }
          </div>
        </div>

        <button
          onClick={() => hasContent && onComplete(promptPreview)}
          disabled={!hasContent}
          className={`mt-6 w-full py-4 rounded-2xl font-extrabold text-base transition-all duration-200 cursor-pointer whitespace-nowrap ${
            hasContent ? "bg-[#00D873] text-white hover:opacity-90 hover:-translate-y-0.5" : "bg-[#E0E2E6] text-[#8B95A1] cursor-not-allowed"
          }`}
          style={hasContent ? { boxShadow: "0 6px 24px rgba(0,216,115,0.3)" } : {}}
        >
          {hasContent ? "프롬프트 완성! 결과물 제출하기 →" : "블록을 최소 1개 이상 선택해주세요"}
        </button>
      </div>
    </>
  );
}
